import React, { useEffect, useState } from 'react'
import {
  CrossSellPricingHeader,
  CrossSellPricingMain,
  CrossSellPricingStyled,
  CrossSellPricingUpTo,
  CrossSellPricingWrapper,
  CrossSellShowHideFeaturesList,
  CrossSellSliderWrapper,
} from 'Features/CrossSell/CrosSellPricing/CrossSellPricing.styled'
import { useTranslation } from 'react-i18next'

import paidMarketingPlansJson from 'Features/pricing/tabs/PaidMarketing/paidMarketingPlans.json'
import botMitigationPlansJson from 'Features/pricing/tabs/BotMitigation/botMitigationPlans.json'
import { PricingPlansDesktop } from 'Features/pricing/components/PricingPlansDesktop/PricingPlansDesktop'
import { useGetPlans } from 'Features/pricing/api/getPlans'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons'
import { CrossSellFeaturesTable } from 'Features/CrossSell/CrosSellPricing/CrossSellFeaturesTable/CrossSellFeaturesTable'
import { Slider } from '@clickcease/cc-client-components-lib'

import { PLAN_TYPES, PlanTypes } from 'Types/PlansTypes'
import {
  PRODUCT_TYPES,
  I18N_PLATFORMS_NAME,
  ProductTypes,
  ProductTypesWithoutBundle,
} from 'Types/ProductTypes'
import { CURRENCIES, CurrenciesTypes } from 'Types/Currencies'
import { useGetPlansDetails } from 'Features/accountSettings/api/getPlansDetails'
import { CrossSellPlansBox } from 'Features/CrossSell/CrosSellPricing/CrossSellPlansBox/CrossSellPlansBox'
import { botMitigationPlanSizes, paidMarketingPlanSizes } from 'Features/pricing/models/planSizes'
import { UpToVisits } from 'Features/pricing/components/UpToVisits/UpToVisits'
import { PlanTypesSorted } from 'Features/pricing/types/PlanTypesSorted'
import { useGetPlan } from 'Features/pricing/hooks/useGetPlan'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { useIsProductTrial } from 'Hooks/trial/useIsProductTrial'

const { BOT_ZAPPING, PAID_MARKETING } = PRODUCT_TYPES

interface CrossSellPricingProps {
  source?: string
}

export const CrossSellPricing = ({ source = 'upgrade' }: CrossSellPricingProps) => {
  const { t } = useTranslation('pricing')
  const isProductTrial = useIsProductTrial()
  const [showFeatureTable, setShowFeatureTable] = useState<boolean>(false)
  const { data: planDetails } = useGetPlansDetails()
  const [plansByType, setPlansByType] = useState<PlanTypesSorted>({} as PlanTypesSorted)
  const [pmPlanType, setPmPlanType] = useState<PlanTypes>(PLAN_TYPES.CORE)
  const [bmPlanType, setBmPlanType] = useState<PlanTypes>(PLAN_TYPES.CORE)
  const [bmNumOfVisits, setBmNumOfVisits] = useState<number>(200)
  const [pmNumOfVisits, setPmNumOfVisits] = useState<number>(100)
  const [productType, setProductType] = useState<ProductTypes | undefined>()
  const [currency, setCurrency] = useState<CurrenciesTypes>(CURRENCIES.USD)
  const [billingCycleInMonths, setBillingCycleInMonths] = useState<number>(12)

  const plans = useGetPlans()
  const { sortPlansByType } = useGetPlan()

  useEffect(() => {
    if (!(plans.data instanceof Error)) {
      setPlansByType(sortPlansByType(plans.data || []))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans.data])

  const setPageData = () => {
    const planDetailsNoAddons = planDetails?.plans.filter((item) => item.type !== 'Addon')
    const firstPlan = planDetailsNoAddons?.[0]
    setCurrency((firstPlan?.currency as CurrenciesTypes) || CURRENCIES.USD)
    if (firstPlan) {
      setBillingCycleInMonths(firstPlan.billingCycleInMonths)
    }
    if (firstPlan?.product.toLowerCase() === BOT_ZAPPING) {
      setProductType(PAID_MARKETING)
      setBmPlanType(firstPlan?.type as PlanTypes)
      setBmNumOfVisits(firstPlan?.limit / 1000)
    } else if (firstPlan?.product.toLowerCase() === PAID_MARKETING) {
      setProductType(BOT_ZAPPING)
      setPmPlanType(firstPlan?.type as PlanTypes)
      setPmNumOfVisits(firstPlan?.limit / 1000)
    }
  }

  useEffect(() => {
    setPageData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planDetails])

  const sliderData = {
    defaultValue: productType === PAID_MARKETING ? bmNumOfVisits : pmNumOfVisits,
    min: productType === PAID_MARKETING ? 100 : 200,
    max: productType === PAID_MARKETING ? 1250 : 1750,
    steps: productType === PAID_MARKETING ? paidMarketingPlanSizes : botMitigationPlanSizes,
  }

  const onSliderChange = (numOfVisits: number) => {
    productType === PAID_MARKETING ? setPmNumOfVisits(numOfVisits) : setBmNumOfVisits(numOfVisits)
  }

  const pageTitle = t(
    `cross-sell.cross_sale_pricing.${
      productType === PAID_MARKETING
        ? I18N_PLATFORMS_NAME[PAID_MARKETING]
        : I18N_PLATFORMS_NAME[BOT_ZAPPING]
    }_title${
      isProductTrial.isProductAllowedTrial(productType as ProductTypesWithoutBundle) ? '_trial' : ''
    }`
  )
  return (
    <CrossSellPricingStyled>
      <CrossSellPricingHeader>{pageTitle}</CrossSellPricingHeader>
      <CrossSellPricingWrapper>
        <CrossSellPricingMain>
          <CrossSellPricingUpTo>
            <UpToVisits
              numOfVisits={productType === PAID_MARKETING ? pmNumOfVisits : bmNumOfVisits}
              maxVisits={productType === PAID_MARKETING ? 1000 : 1500}
            />
          </CrossSellPricingUpTo>
          <CrossSellSliderWrapper>
            <Slider
              width={'520px'}
              min={sliderData.min}
              max={sliderData.max}
              previousValue={sliderData.defaultValue || 0}
              value={productType === PAID_MARKETING ? pmNumOfVisits : bmNumOfVisits}
              steps={sliderData.steps}
              minUnit={'k'}
              maxUnit={t('bundles.million')}
              maxText={t('bundles.million_number', {
                number: productType === PAID_MARKETING ? 1 : 1.5,
              })}
              onChange={(value) => onSliderChange(value as number)}
            />
          </CrossSellSliderWrapper>
          {plansByType && (
            <PricingPlansDesktop
              numOfVisits={productType === PAID_MARKETING ? pmNumOfVisits : bmNumOfVisits}
              crossSell={true}
              plansDb={
                productType === PAID_MARKETING
                  ? plansByType.paidMarketing
                  : plansByType.botMitigation
              }
              setSelectedPlan={(plan) => {
                productType === PAID_MARKETING ? setPmPlanType(plan) : setBmPlanType(plan)
              }}
              selectedPlan={productType === PAID_MARKETING ? pmPlanType : bmPlanType}
              plansJson={
                productType === PAID_MARKETING ? paidMarketingPlansJson : botMitigationPlansJson
              }
              productName={productType || PAID_MARKETING}
              currency={currency}
              source={source}
            />
          )}
          <CrossSellShowHideFeaturesList onClick={() => setShowFeatureTable(!showFeatureTable)}>
            {t('bundles.complete_features_list')}
            <FontAwesomeIcon
              icon={showFeatureTable ? (faChevronUp as IconProp) : (faChevronDown as IconProp)}
              style={{ marginLeft: '15px', height: '11px' }}
            />
          </CrossSellShowHideFeaturesList>
          {showFeatureTable && productType && (
            <CrossSellFeaturesTable productType={productType} source={source} />
          )}
        </CrossSellPricingMain>
        <CrossSellPlansBox
          css={{ margin: '0px 0px 0px 20px', height: 'fit-content' }}
          plansDb={
            productType === PAID_MARKETING ? plansByType.paidMarketing : plansByType.botMitigation
          }
          productType={productType as ProductTypesWithoutBundle}
          paidMarketingPlan={pmPlanType}
          paidMarketingVisits={pmNumOfVisits}
          botMitigationPlan={bmPlanType}
          botMitigationVisits={bmNumOfVisits}
          currency={currency}
          billingCycleMonths={billingCycleInMonths}
          source={source}
        />
      </CrossSellPricingWrapper>
    </CrossSellPricingStyled>
  )
}
